import React, { useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import './Modal.scss';

interface ModalProps {
  isOpen?: boolean; // Made optional for internal state handling
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  // New props for internal state handling
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  preventClose?: boolean; // Prevent closing via ESC key or overlay click
}

const Modal: React.FC<ModalProps> = ({
  isOpen: externalIsOpen,
  title,
  children,
  onClose,
  className = '',
  size = 'medium',
  defaultOpen = false,
  onOpenChange,
  preventClose = false
}) => {
  // Internal state for when isOpen is not provided
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);
  const titleId = useId();

  // Determine if we're using external or internal state
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  // Handle internal state changes
  const handleInternalClose = () => {
    if (preventClose) {
      return; // Don't close if preventClose is true
    }
    if (!isControlled) {
      setInternalIsOpen(false);
    }
    onOpenChange?.(false);
    onClose?.();
  };

  const handleInternalOpen = () => {
    if (!isControlled) {
      setInternalIsOpen(true);
    }
    onOpenChange?.(true);
  };

  // Sync internal state with external state changes
  useEffect(() => {
    if (isControlled && externalIsOpen !== undefined) {
      setInternalIsOpen(externalIsOpen);
    }
  }, [externalIsOpen, isControlled]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleInternalClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isOpen, handleInternalClose, preventClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleInternalClose();
    }
  };

  const modalContent = (
    <FocusTrap active={true} focusTrapOptions={{
      initialFocus: false,
      escapeDeactivates: false,
      allowOutsideClick: true,
      returnFocusOnDeactivate: true,
      tabbableOptions: { displayCheck: 'none' },
    }}>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div
          className={`modal ${size} ${className}`}
          role="dialog"
          aria-modal="true"
          {...(title ? { 'aria-labelledby': titleId } : {})}
        >
          <div className="modal__header">
            {title && <h3 className="modal__title" id={titleId}>{title}</h3>}
            <button className="modal__close" onClick={handleInternalClose} aria-label="Close modal" type="button">
              ×
            </button>
          </div>
          <div className="modal__content">{children}</div>
        </div>
      </div>
    </FocusTrap>
  );

  const modalRoot = document.getElementById('modal-root');

  if (!modalRoot) {
    console.warn('Modal root element not found. Modal will render in place.');
    return modalContent;
  }

  return createPortal(modalContent, modalRoot);
};

export default Modal;
